import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiMail, FiLock, FiAlertCircle, FiArrowRight, FiMoon, FiSun } from 'react-icons/fi';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, isDarkMode, toggleDarkMode } = useAppContext();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    if (result.success) {
      navigate('/');
    } else {
      setError(result.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fcfaff] dark:bg-dark-bg flex items-center justify-center p-4 relative overflow-hidden transition-colors duration-300">
      {/* Background blobs */}
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-brand-200/30 dark:bg-brand-900/20 blur-[120px] rounded-full"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-brand-100/30 dark:bg-brand-800/10 blur-[120px] rounded-full"></div>

      {/* Dark mode toggle */}
      <button
        onClick={toggleDarkMode}
        className="absolute top-5 right-5 p-2.5 rounded-xl bg-white dark:bg-dark-surface border border-gray-100 dark:border-dark-border text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-card transition-all shadow-sm z-10"
      >
        <SafeIcon icon={isDarkMode ? FiSun : FiMoon} className="w-5 h-5" />
      </button>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        <div className="text-center mb-10">
          <div className="w-20 h-20 bg-brand-600 rounded-[2rem] flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-brand-200 dark:shadow-brand-900/30 rotate-3">
            <span className="text-white font-black text-4xl leading-none tracking-tighter">N</span>
          </div>
          <h1 className="text-4xl font-black text-brand-950 dark:text-white tracking-tight mb-2 uppercase">WELCOME BACK</h1>
          <p className="text-gray-400 dark:text-dark-muted font-bold uppercase tracking-widest text-[10px]">Log in to find your next Nakama</p>
        </div>

        <div className="bg-white dark:bg-dark-surface rounded-[40px] p-8 shadow-2xl shadow-brand-900/5 dark:shadow-black/20 border border-white dark:border-dark-border transition-colors">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label className="block text-[10px] font-black text-gray-400 dark:text-dark-muted uppercase tracking-widest ml-1">Email</label>
              <div className="relative group">
                <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-brand-600 transition-colors" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@nakama.gg"
                  required
                  className="w-full pl-12 pr-4 py-4 bg-gray-50 dark:bg-dark-card border border-gray-100 dark:border-dark-border rounded-2xl font-bold text-sm outline-none focus:ring-4 focus:ring-brand-500/10 focus:border-brand-500 dark:focus:border-brand-500/50 transition-all dark:text-white dark:placeholder-gray-600"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-[10px] font-black text-gray-400 dark:text-dark-muted uppercase tracking-widest ml-1">Password</label>
              <div className="relative group">
                <SafeIcon icon={FiLock} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-brand-600 transition-colors" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full pl-12 pr-4 py-4 bg-gray-50 dark:bg-dark-card border border-gray-100 dark:border-dark-border rounded-2xl font-bold text-sm outline-none focus:ring-4 focus:ring-brand-500/10 focus:border-brand-500 dark:focus:border-brand-500/50 transition-all dark:text-white dark:placeholder-gray-600"
                />
              </div>
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex items-center gap-2 text-red-500 bg-red-50 dark:bg-red-900/20 p-4 rounded-2xl border border-red-100 dark:border-red-900/30"
              >
                <SafeIcon icon={FiAlertCircle} />
                <span className="text-xs font-bold">{error}</span>
              </motion.div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-brand-600 text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-brand-200 dark:shadow-brand-900/20 hover:bg-brand-700 active:scale-95 transition-all flex items-center justify-center gap-2 group disabled:opacity-70"
            >
              {loading ? (
                <div className="w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
              ) : (
                <>
                  Join the Team
                  <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>

          <div className="mt-8 pt-8 border-t border-gray-50 dark:border-dark-border text-center">
            <p className="text-xs text-gray-400 dark:text-dark-muted font-bold uppercase tracking-widest">
              Don't have an account?{' '}
              <Link to="/register" className="text-brand-600 dark:text-brand-400 hover:underline">Register</Link>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}